import React from 'react';
import { safeString, safeUrl, formatCurrency } from '@/src/utils/helpers';
import { HeartIcon, CalendarIcon, HomeIcon, NoteIcon } from '@/src/components/icons';
import { Card, CardHeader, CardContent, CardTitle, CardDescription } from '@/src/components/ui/card';
import { Badge } from '@/src/components/ui/badge';

const PropertyCard = ({ property, onPropertyClick, toggleFavorite, isFavorite, getPropertyTags, onOpenTagManager, hasNote }) => {
  
  try {
    if (!property || typeof property !== 'object') {
      return (
        <Card>
          <CardContent>
            <p className="text-red-600">Invalid property data</p>
          </CardContent>
        </Card>
      );
    }
    
    const address = safeString(property.full_address, 'Unknown Address');
    
    const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address + ', Winnipeg, MB, Canada')}`;
    
    const addressSlug = safeUrl(property.full_address);
    const honestDoorUrl = addressSlug ? `https://www.honestdoor.com/property/${addressSlug}-winnipeg-mb` : null;
  
  return (
    <Card 
      className="hover:shadow-md transition-all duration-200 hover:-translate-y-0.5 cursor-pointer"
      onClick={() => onPropertyClick(property)}
    >
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle>
              {address}
            </CardTitle>
            <CardDescription>
              {safeString(property.neighbourhood_area, 'Unknown Area')}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('Tag button clicked', { property: property.roll_number, onOpenTagManager });
                onOpenTagManager && onOpenTagManager(property);
              }}
              className="p-1.5 rounded-full transition-colors duration-200 hover:bg-gray-100 text-gray-400 hover:text-gray-600"
              title="Manage tags"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2H2v10l9.29 9.29a1 1 0 0 0 1.42 0L22 12.29a1 1 0 0 0 0-1.42L12 2z"></path>
                <circle cx="7" cy="7" r="3"></circle>
              </svg>
            </button>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                toggleFavorite(property.roll_number);
              }}
              className={`p-1.5 rounded-full transition-colors duration-200 hover:bg-gray-100 ${
                isFavorite(property.roll_number) 
                  ? 'text-red-500 hover:text-red-600' 
                  : 'text-gray-400 hover:text-gray-600'
              }`}
              title={isFavorite(property.roll_number) ? 'Remove from favorites' : 'Add to favorites'}
            >
              <HeartIcon filled={isFavorite(property.roll_number)} className="w-5 h-5" />
            </button>
            <span className="text-xs bg-stone-100 text-stone-800 px-2 py-1 rounded">
              {property.roll_number}
            </span>
          </div>
        </div>
        
        <div className="flex items-center gap-3 flex-wrap mt-3">
          <a 
            href={googleMapsUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="text-xs text-stone-600 hover:text-stone-800 hover:underline flex items-center"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
              <circle cx="12" cy="10" r="3"></circle>
            </svg>
            Google Maps
          </a>
          {honestDoorUrl && (
            <a 
              href={honestDoorUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="text-xs text-stone-600 hover:text-stone-800 hover:underline flex items-center"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1">
                <path d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"></path>
              </svg>
              HonestDoor
            </a>
          )}
          {property.detail_url && property.detail_url.url && (
            <a 
              href={property.detail_url.url} 
              target="_blank" 
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="text-xs text-stone-600 hover:text-stone-800 hover:underline flex items-center"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14,2 14,8 20,8"></polyline>
                <line x1="16" y1="13" x2="8" y2="13"></line>
                <line x1="16" y1="17" x2="8" y2="17"></line>
                <polyline points="10,9 9,9 8,9"></polyline>
              </svg>
              City Assessment
            </a>
          )}
        </div>
      </CardHeader>
    
      <CardContent>
        <div className="mb-4">
          <p className="text-2xl font-bold text-stone-900">
            {formatCurrency(property.total_assessed_value)}
          </p>
        </div>
        
        <div className="grid grid-cols-2 gap-3 text-sm text-stone-600 mb-4">
          <div className="flex items-center">
            <CalendarIcon />
            <span className="ml-1">Built: {safeString(property.year_built, 'N/A')}</span>
          </div>
          <div className="flex items-center">
            <HomeIcon />
            <span className="ml-1">{safeString(property.total_living_area, 'N/A')} sq ft</span>
          </div>
          <div className="col-span-2 text-xs text-stone-500">
            {safeString(property.property_use_code, 'N/A')}
          </div>
        </div>
        
        {getPropertyTags && (() => {
          const tags = getPropertyTags(property.roll_number);
          if (tags && tags.length > 0) {
            return (
              <div className="mb-3">
                <div className="flex flex-wrap gap-1">
                  {tags.map((tag) => (
                    React.createElement(Badge, {
                      key: tag.id,
                      className: "text-xs",
                      style: { backgroundColor: tag.color, color: '#ffffff', border: 'none' },
                      onClick: (e) => e.stopPropagation()
                    }, tag.name)
                  ))}
                </div>
              </div>
            );
          }
          return null;
        })()}
        
        {hasNote && hasNote(property.roll_number) && (
          <div className="mb-3 flex items-center gap-1 text-xs text-amber-600">
            <NoteIcon className="w-3.5 h-3.5" />
            <span>Has note</span>
          </div>
        )}

        <div className="flex flex-wrap gap-1">
          {property.basement === 'Yes' && (
            <span className="text-xs bg-gray-100 text-gray-800 px-2 py-1 rounded">Basement</span>
          )}
          {(property.attached_garage === 'Yes' || property.detached_garage === 'Yes') && (
            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">Garage</span>
          )}
          {property.pool === 'Yes' && (
            <span className="text-xs bg-cyan-100 text-cyan-800 px-2 py-1 rounded">Pool</span>
          )}
          {property.air_conditioning === 'Yes' && (
            <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">A/C</span>
          )}
        </div>
      </CardContent>
    </Card>
  );
  } catch (error) {
    console.error('Error in PropertyCard:', error, 'Property:', property);
    return (
      <Card>
        <CardContent>
          <p className="text-red-600">Error displaying property</p>
          <p className="text-xs text-gray-500 mt-1">Check console for details</p>
        </CardContent>
      </Card>
    );
  }
};

export default PropertyCard;